/**
 * MAPPING（02 測光）
 *
 * - モノクロ写真（撮影直後は現像液の中のように浮かび上がる）
 * - 空いた場所をタップでピンを置き、すぐにピン編集シートを開く
 *   1 本目は主要シャドウ、2 本目は主要ハイライト、3 本目以降は役なし（ゾーン V）
 * - ピンを長押し（0.38 秒）してドラッグで移動。タップで編集
 * - 右上「テンプレート」、写真の上「クロップ」「撮り直す」
 * - 左上 × は下書きを残して閉じる（撮影直後から自動保存している）
 * - 下端「NEXT 03 露出を計算」（ピンが 0 本なら無効）
 */

import {
  showScreen, resetScreen, escapeHtml, toast, zoneToRoman, setLoading, icon, buzz,
  confirmDialog, renderRail, blobUrl, releaseUrls, chapterLink,
} from './ui.js';
import {
  getCurrentShoot, addSpot, updateSpot, subscribe, updateCurrentShoot,
  clearCurrentShoot, flushSave, committedSpots, onSaved, pushSpotUndo, undoSpots, canUndoSpots,
} from './scene.js';
import { computeWeightsFromPreset, blobToMonoBlob } from './mono.js';
import { getAspectRatio } from './presets.js';
import { presetMeta, formatLabel } from './gear.js';
import { cropToBlob, blobToImage, calculateCenterCrop } from './crop.js';
import { deleteShoot } from './store.js';

const LONG_PRESS_MS = 380;
const TAP_MOVE_TOLERANCE = 8;

let cropState = null;
let quickPlace = false; // 5. 連続配置：タップでピンを置くだけ（編集シートを開かない） // { img, url, W, H, crop, aspect }
let unsub = null;
let unsubSaved = null;

export async function openMapping(opts = {}) {
  const shoot = getCurrentShoot();
  if (!shoot) { toast('シーンがありません'); return; }
  cropState = null;
  render(!!opts.fresh);
  showScreen('mapping', { back: !!opts.back });
  bindScene();
}

/** 既存シーンを開き直す（ピン編集から戻る・工程レールから） */
export function reopenMapping(opts = { back: true }) {
  return openMapping(opts);
}

function bindScene() {
  unsub?.();
  unsubSaved?.();
  unsub = subscribe(() => {
    if (document.getElementById('screen-mapping')?.classList.contains('screen--active') && !cropState) {
      renderPins();
      renderList();
    }
  });
  unsubSaved = onSaved(() => {
    const st = document.getElementById('map-saved');
    if (st) st.textContent = '下書き · 自動保存済み';
  });
}

// --------------------------------------------------
// 描画
// --------------------------------------------------
function render(fresh) {
  const el = document.getElementById('screen-mapping');
  const shoot = getCurrentShoot();
  releaseUrls('mapping');
  el.innerHTML = `
    <div class="safe-top"></div>
    <div class="bar" style="padding:0 10px 0 8px">
      <button class="bar__btn" type="button" data-map-close aria-label="下書きを保存して閉じる">${icon('close', 22)}</button>
      <div style="flex:1;min-width:0"><div style="font:600 15px/1.2 var(--font-jp-sans)">測光</div><div class="sub" id="map-saved" style="font:400 11px/1.3 var(--font-jp-sans)">下書き · 自動保存</div></div>
      <button class="bar__boxbtn" type="button" data-tpl><svg class="i" width="20" height="10" aria-hidden="true"><use href="#i-tpl"/></svg>テンプレート</button>
    </div>
    <div id="map-rail"></div>
    <div class="body" id="map-body">
      <div class="map-tools">
        <span class="map-tools__sum">${escapeHtml(presetMeta(shoot))}</span>
        <button class="map-tools__btn" type="button" data-undo hidden>${icon('reset', 15)}取り消す</button>
        <button class="map-tools__btn" type="button" data-crop ${shoot.source === 'no-photo' ? 'hidden' : ''}>${icon('crop', 15)}クロップ</button>
        <button class="map-tools__btn" type="button" data-retake>${icon('reset', 15)}撮り直す</button>
      </div>
      <div class="map-stage">
        <div class="map-photo photo ${fresh ? 'photo--develop' : ''}" id="map-photo">
          <img id="map-img" alt="撮影したシーン（モノクロ）" src="${blobUrl('mapping', shoot.monoImageBlob || shoot.croppedImageBlob)}" draggable="false" />
          <span class="map-photo__tag">${shoot.source === 'no-photo' ? '写真なし' : shoot.source === 'photo-library' ? 'FROM PHOTOS' : 'MONO CAPTURE'} · ${escapeHtml(formatLabel(shoot.filmFormat))}</span>
        </div>
      </div>
      <div class="map-help" style="align-items:center"><span id="map-help-tap">${quickPlace ? '連続配置中：タップで次々に置く' : '空いた場所をタップ：ピンを追加'}</span><span>長押しでドラッグ：移動</span>
        <button type="button" class="pill pill--sm ${quickPlace ? 'is-on' : ''}" data-quick style="margin-left:auto;height:30px">連続配置</button></div>
      <div class="chapter-links" style="margin-top:4px">${chapterLink('zones-detail', '02 11 ゾーンの理解')}${chapterLink('scene-approach', '19 シーン別アプローチ')}</div>
      <div id="map-list"></div>
    </div>
    <div class="foot" id="map-foot"></div>
  `;
  renderRail(el.querySelector('#map-rail'), 1, {});
  renderFoot();

  const photo = el.querySelector('#map-photo');
  if (fresh) {
    requestAnimationFrame(() => requestAnimationFrame(() => photo.classList.add('is-developed')));
  }
  el.querySelector('#map-img').addEventListener('load', () => renderPins());
  photo.addEventListener('pointerdown', onPointerDown);
  photo.addEventListener('pointermove', onPointerMove);
  photo.addEventListener('pointerup', onPointerUp);
  photo.addEventListener('pointercancel', onPointerCancel);

  el.querySelector('[data-map-close]').addEventListener('click', async () => {
    try { await flushSave(); toast('下書きを保存しました'); } catch { toast('保存に失敗しました'); }
    resetScreen('home');
  });
  el.querySelector('[data-tpl]').addEventListener('click', () => {
    import('./scene-templates.js').then((m) => m.openSceneTemplates());
  });
  el.querySelector('[data-crop]').addEventListener('click', enterCropMode);
  el.querySelector('[data-quick]').addEventListener('click', (e) => {
    quickPlace = !quickPlace;
    buzz(6);
    e.currentTarget.classList.toggle('is-on', quickPlace);
    el.querySelector('#map-help-tap').textContent = quickPlace ? '連続配置中：タップで次々に置く' : '空いた場所をタップ：ピンを追加';
    toast(quickPlace ? 'タップでピンを次々に置けます。測光値は一覧から「決定して次へ」で順に入れてください' : '連続配置を終えました', quickPlace ? 3800 : 2000);
  });
  el.querySelector('[data-undo]').addEventListener('click', () => {
    const label = undoSpots();
    if (label) { buzz(8); toast(`${label}を取り消しました`); }
  });
  el.querySelector('[data-retake]').addEventListener('click', async () => {
    const s = getCurrentShoot();
    const n = committedSpots(s).length;
    const ok = await confirmDialog({
      title: '撮り直しますか？',
      body: `この写真と配置した ${n} 個のピン${s?.development || s?.printing ? '、現像とプリントの記録' : ''}を破棄して、機材の選択に戻ります。この操作は取り消せません。`,
      ok: '破棄して撮り直す',
    });
    if (!ok) return;
    try { if (s) await deleteShoot(s.id); } catch (e) { console.warn(e); }
    clearCurrentShoot();
    toast('写真とピンを破棄しました');
    resetScreen('select-gear');
  });

  renderPins();
  renderList();
}

function renderFoot() {
  const foot = document.getElementById('map-foot');
  if (!foot) return;
  if (cropState) {
    foot.innerHTML = `
      <div class="crop-foot">
        <button type="button" data-crop-center>中央に戻す</button>
        <button type="button" data-crop-cancel>取消</button>
        <button type="button" class="is-primary" data-crop-apply>この位置で適用</button>
      </div>`;
    foot.querySelector('[data-crop-center]').addEventListener('click', () => {
      cropState.crop = calculateCenterCrop(cropState.W, cropState.H, cropState.aspect);
      drawCropFrame();
    });
    foot.querySelector('[data-crop-cancel]').addEventListener('click', exitCropMode);
    foot.querySelector('[data-crop-apply]').addEventListener('click', applyCrop);
    return;
  }
  const has = committedSpots().length > 0;
  foot.innerHTML = `
    <button class="btn-primary btn-primary--next ${has ? '' : 'is-disabled'}" type="button" data-next>
      <span class="btn-primary__en">NEXT 03</span><span class="btn-primary__label">露出を計算</span>${icon('arrow')}
    </button>`;
  foot.querySelector('[data-next]').addEventListener('click', () => {
    if (!committedSpots().length) { toast('スポットを 1 つ以上配置してください'); return; }
    import('./exposure.js').then((m) => m.openExposure());
  });
}

/** ピンを差分で描く（ドラッグ中に落下の動きが繰り返されないように） */
function renderPins() {
  const photo = document.getElementById('map-photo');
  const shoot = getCurrentShoot();
  if (!photo || !shoot || cropState) return;
  const spots = shoot.spots;
  const seen = new Set();
  let n = 0;
  spots.forEach((s) => {
    if (!s._draft) n++;
    const num = s._draft ? committedSpots().length + 1 : n;
    seen.add(s.id);
    let pin = photo.querySelector(`.pin[data-spot-id="${CSS.escape(s.id)}"]`);
    if (!pin) {
      pin = document.createElement('div');
      pin.className = 'pin';
      pin.dataset.spotId = s.id;
      pin.innerHTML = '<div class="pin__in"><div class="pin__ring"></div><div class="pin__dot"></div><div class="pin__tag"></div></div>';
      photo.appendChild(pin);
    }
    const role = s.role === 'shadow-anchor' ? 'S' : s.role === 'highlight-anchor' ? 'H' : '';
    pin.style.left = `${s.x * 100}%`;
    pin.style.top = `${s.y * 100}%`;
    pin.classList.toggle('is-anchor', !!role);
    pin.classList.toggle('is-draft', !!s._draft);
    pin.classList.toggle('is-drag', ps.dragging && ps.pinId === s.id);
    pin.querySelector('.pin__tag').textContent = `${num}${role ? ` ${role}` : ''}`;
  });
  photo.querySelectorAll('.pin').forEach((p) => { if (!seen.has(p.dataset.spotId)) p.remove(); });
  const undoBtn = document.querySelector('#screen-mapping [data-undo]');
  if (undoBtn) undoBtn.hidden = !canUndoSpots();

  let hint = photo.querySelector('.map-hint');
  if (spots.length === 0) {
    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'map-hint';
      hint.innerHTML = '<span class="map-hint__ring"></span><span class="map-hint__t">写真をタップしてピンを置く</span>';
      photo.appendChild(hint);
    }
  } else hint?.remove();
}

function renderList() {
  const box = document.getElementById('map-list');
  if (!box) return;
  const spots = committedSpots();
  renderFoot();
  if (spots.length === 0) {
    box.innerHTML = `
      <div class="map-empty">
        <div style="font:500 16px/1.5 var(--font-jp-serif)">写真をタップしてスポット測光ピンを配置してください。</div>
        <p class="sub" style="margin:6px 0 0;font-size:12.5px;line-height:1.7">1 本目は主要シャドウ、2 本目は主要ハイライトとして置かれます。</p>
      </div>`;
    return;
  }
  box.innerHTML = `
    <div class="spots-head"><span class="eyebrow">spots · ${spots.length}</span><span class="sub" style="font:400 12px/1 var(--font-jp-sans)">EV · 配置ゾーン</span></div>
    ${spots.map((s, i) => {
      const anchor = s.role === 'shadow-anchor' || s.role === 'highlight-anchor';
      const roleT = s.role === 'shadow-anchor' ? '主要シャドウ' : s.role === 'highlight-anchor' ? '主要ハイライト' : '';
      return `
        <button class="spot-row" type="button" data-spot="${escapeHtml(s.id)}">
          <span class="spot-row__n ${anchor ? 'is-anchor' : ''}">${i + 1}</span>
          <div class="spot-row__body"><div class="spot-row__label">${escapeHtml(s.label || `スポット ${i + 1}`)}</div>${roleT ? `<div class="spot-row__role">${roleT}</div>` : ''}</div>
          <span class="spot-row__ev">${Number(s.meterEV).toFixed(1)}</span>
          <span class="spot-row__z zc-${s.placedZone}" style="color:${s.placedZone <= 5 ? 'var(--zone-10)' : 'var(--zone-0)'}">${zoneToRoman(s.placedZone)}</span>
        </button>`;
    }).join('')}
    <div style="height:20px"></div>`;
  box.querySelectorAll('[data-spot]').forEach((b) => b.addEventListener('click', () => openSpotEditor(b.dataset.spot)));
}

// --------------------------------------------------
// ポインター操作（写真の実表示矩形を基準に正規化する）
// --------------------------------------------------
const ps = { down: false, x: 0, y: 0, pinId: null, dragging: false, timer: null };

function photoRect() {
  return document.getElementById('map-img')?.getBoundingClientRect() || null;
}

function onPointerDown(e) {
  if (cropState) return;
  ps.down = true;
  ps.x = e.clientX;
  ps.y = e.clientY;
  ps.dragging = false;
  const pinEl = e.target.closest('.pin');
  ps.pinId = pinEl ? pinEl.dataset.spotId : null;
  try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  if (ps.pinId) {
    clearTimeout(ps.timer);
    ps.timer = setTimeout(() => {
      pushSpotUndo('ピンの移動');
      ps.dragging = true;
      pinEl.classList.add('is-drag');
      buzz(15);
    }, LONG_PRESS_MS);
  }
}

function onPointerMove(e) {
  if (!ps.down) return;
  const moved = Math.hypot(e.clientX - ps.x, e.clientY - ps.y);
  if (!ps.dragging && moved > TAP_MOVE_TOLERANCE) clearTimeout(ps.timer);
  if (ps.dragging && ps.pinId) {
    const r = photoRect();
    if (!r) return;
    const x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const y = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
    updateSpot(ps.pinId, { x, y });
  }
}

function onPointerUp(e) {
  if (!ps.down) return;
  clearTimeout(ps.timer);
  const moved = Math.hypot(e.clientX - ps.x, e.clientY - ps.y);
  if (ps.dragging && ps.pinId) {
    const id = ps.pinId;
    resetPointer();
    document.querySelector(`.pin[data-spot-id="${CSS.escape(id)}"]`)?.classList.remove('is-drag');
    toast('ピンを移動しました');
    return;
  } else if (ps.pinId && moved < TAP_MOVE_TOLERANCE) {
    openSpotEditor(ps.pinId);
  } else if (!ps.pinId && moved < TAP_MOVE_TOLERANCE) {
    const r = photoRect();
    if (r) {
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      if (x >= 0 && x <= 1 && y >= 0 && y <= 1) placePin(x, y);
    }
  }
  resetPointer();
}

function onPointerCancel() {
  clearTimeout(ps.timer);
  if (ps.pinId) document.querySelector(`.pin[data-spot-id="${CSS.escape(ps.pinId)}"]`)?.classList.remove('is-drag');
  resetPointer();
}

function resetPointer() {
  ps.down = false;
  ps.dragging = false;
  ps.pinId = null;
}

function placePin(x, y) {
  const shoot = getCurrentShoot();
  if (!shoot) return;
  const done = committedSpots(shoot);
  const n = done.length;
  const role = n === 0 ? 'shadow-anchor' : n === 1 ? 'highlight-anchor' : null;
  const zone = role === 'shadow-anchor' ? (shoot.defaultShadowZone ?? 3)
    : role === 'highlight-anchor' ? (shoot.defaultHighlightZone ?? 8) : 5;
  const lastEv = done.length ? done[done.length - 1].meterEV : 12;
  if (quickPlace) {
    // 連続配置：確定したピンとして置き、測光値はあとで「決定して次へ」で順に入れる
    pushSpotUndo('ピンの追加');
    addSpot({ x, y, role, placedZone: zone, meterEV: lastEv });
    ripple(x, y);
    buzz(8);
    return;
  }
  // 編集シートで「決定」するまでは下書きのピン（閉じると消える）
  const spot = addSpot({ x, y, role, placedZone: zone, meterEV: lastEv, _draft: true });
  ripple(x, y);
  buzz(8);
  if (spot) openSpotEditor(spot.id, true);
}

function ripple(x, y) {
  const photo = document.getElementById('map-photo');
  if (!photo) return;
  const r = document.createElement('span');
  r.className = 'ripple';
  r.style.left = `${x * 100}%`;
  r.style.top = `${y * 100}%`;
  photo.appendChild(r);
  setTimeout(() => r.remove(), 750);
}

function openSpotEditor(spotId, isNew = false) {
  import('./meter.js').then((m) => m.openMeter(spotId, { isNew }));
}

// ====================================================================
// クロップ（元画像の上で枠の位置を動かす。比率と大きさは固定）
// ====================================================================
async function enterCropMode() {
  const shoot = getCurrentShoot();
  if (!shoot?.originalImageBlob) { toast('元画像がありません'); return; }
  const img = await blobToImage(shoot.originalImageBlob);
  const W = img.naturalWidth;
  const H = img.naturalHeight;
  const aspect = getAspectRatio(shoot, shoot.meta?.orientation);
  const base = shoot.cropBox && shoot.cropBox.w ? { ...shoot.cropBox } : calculateCenterCrop(W, H, aspect);
  cropState = { img, W, H, crop: base, aspect };
  const photo = document.getElementById('map-photo');
  photo.querySelectorAll('.pin, .map-hint').forEach((p) => p.remove());
  const tag = photo.querySelector('.map-photo__tag');
  if (tag) tag.hidden = true;
  const imgEl = document.getElementById('map-img');
  imgEl.src = blobUrl('mapping', shoot.originalImageBlob);
  await new Promise((r) => { if (imgEl.complete) r(); else imgEl.onload = r; });
  const list = document.getElementById('map-list');
  if (list) { list.style.opacity = '.4'; list.style.pointerEvents = 'none'; }
  const layer = document.createElement('div');
  layer.className = 'crop-layer';
  layer.innerHTML = `<div class="crop-frame" id="crop-frame">
    <span class="crop-frame__c" style="left:-3px;top:-3px;border-left-width:4px;border-top-width:4px"></span>
    <span class="crop-frame__c" style="right:-3px;top:-3px;border-right-width:4px;border-top-width:4px"></span>
    <span class="crop-frame__c" style="left:-3px;bottom:-3px;border-left-width:4px;border-bottom-width:4px"></span>
    <span class="crop-frame__c" style="right:-3px;bottom:-3px;border-right-width:4px;border-bottom-width:4px"></span>
    <span class="crop-frame__t">ドラッグで移動 · ${escapeHtml(formatLabel(shoot.filmFormat))} 固定</span>
  </div>`;
  photo.appendChild(layer);
  photo.style.overflow = 'hidden';
  let drag = null;
  layer.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    try { layer.setPointerCapture(e.pointerId); } catch {}
    drag = { x: e.clientX, y: e.clientY, cx: cropState.crop.x, cy: cropState.crop.y };
  });
  layer.addEventListener('pointermove', (e) => {
    if (!drag || !cropState) return;
    const r = imgEl.getBoundingClientRect();
    const sx = r.width / cropState.W;
    const sy = r.height / cropState.H;
    cropState.crop.x = Math.round(Math.max(0, Math.min(cropState.W - cropState.crop.w, drag.cx + (e.clientX - drag.x) / sx)));
    cropState.crop.y = Math.round(Math.max(0, Math.min(cropState.H - cropState.crop.h, drag.cy + (e.clientY - drag.y) / sy)));
    drawCropFrame();
  });
  layer.addEventListener('pointerup', () => { drag = null; });
  layer.addEventListener('pointercancel', () => { drag = null; });
  drawCropFrame();
  renderFoot();
}

function drawCropFrame() {
  const f = document.getElementById('crop-frame');
  if (!f || !cropState) return;
  const c = cropState.crop;
  f.style.left = `${(c.x / cropState.W) * 100}%`;
  f.style.top = `${(c.y / cropState.H) * 100}%`;
  f.style.width = `${(c.w / cropState.W) * 100}%`;
  f.style.height = `${(c.h / cropState.H) * 100}%`;
}

function exitCropMode() {
  cropState = null;
  render(false);
}

async function applyCrop() {
  const shoot = getCurrentShoot();
  if (!shoot || !cropState) { exitCropMode(); return; }
  const st = cropState;
  setLoading('クロップを適用しています');
  try {
    const oldCrop = shoot.cropBox && shoot.cropBox.w ? shoot.cropBox : calculateCenterCrop(st.W, st.H, st.aspect);
    const newCrop = st.crop;
    // 旧クロップ内の正規化座標 → 元画像の絶対座標 → 新クロップ内の正規化座標
    const committed = committedSpots(shoot);
    const newSpots = committed.map((s) => {
      const ax = s.x * oldCrop.w + oldCrop.x;
      const ay = s.y * oldCrop.h + oldCrop.y;
      return { ...s, x: (ax - newCrop.x) / newCrop.w, y: (ay - newCrop.y) / newCrop.h };
    }).filter((s) => s.x >= 0 && s.x <= 1 && s.y >= 0 && s.y <= 1);
    const removed = committed.length - newSpots.length;
    const croppedBlob = await cropToBlob(st.img, newCrop, st.W, st.H);
    const mono = await blobToMonoBlob(croppedBlob, computeWeightsFromPreset(shoot), 1600);
    updateCurrentShoot({ cropBox: newCrop, croppedImageBlob: croppedBlob, monoImageBlob: mono.blob, spots: newSpots });
    cropState = null;
    render(true);
    showScreen('mapping', { anim: 'fade' });
    toast(removed > 0 ? `${removed} 個のピンが範囲外で削除されました` : 'クロップを適用し、モノクロを作り直しました');
  } catch (e) {
    console.error('[crop] apply failed:', e);
    toast('クロップの適用に失敗しました');
    cropState = null;
    render(false);
    showScreen('mapping', { anim: 'fade' });
  }
}
